import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import CategoryBar from '../components/CategoryBar';
import { Loader2, BookOpen } from 'lucide-react';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const BACKEND_URL = '';  // Use relative URLs
const API = '/api';

const NovelsPage = () => {
  useDocumentTitle('Novels');
  const [selectedCategory, setSelectedCategory] = useState('all-novels');
  const [novels, setNovels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNovels();
  }, [selectedCategory]);

  const fetchNovels = async () => {
    setLoading(true);
    try {
      // Fetch from the novels endpoint
      let url = `${API}/novels?limit=50`;
      
      if (selectedCategory !== 'all-novels') {
        url += `&genre=${selectedCategory}`;
      }

      const response = await axios.get(url);
      // The API returns {novels: [...], total: ...}
      setNovels(response.data.novels || []);
    } catch (error) {
      console.error('Error fetching novels:', error);
      setNovels([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Category Bar */}
      <CategoryBar
        type="novel"
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
      />

      {/* Content */}
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-8">
        
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-black text-white mb-2">
            {selectedCategory === 'all-novels' ? 'All Novels' :
             selectedCategory.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
          </h1>
          <p className="text-gray-400">
            Dive into captivating stories and web fiction
          </p>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
          </div>
        ) : novels.length === 0 ? (
          /* Empty State */
          <div className="text-center py-20">
            <div className="text-6xl mb-4">📖</div>
            <h2 className="text-2xl font-bold text-white mb-2">No Novels Yet</h2>
            <p className="text-gray-400 mb-6">Be the first to publish a novel in this category!</p>
            <Link 
              to="/create-novel" 
              className="inline-block bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-3 rounded-lg transition-colors"
            >
              Create Your Novel
            </Link>
          </div>
        ) : (
          /* Novels List */
          <div className="space-y-4">
            {novels.map((novel) => (
              <Link
                key={novel.id}
                to={`/novel/${novel.id}`}
                className="block bg-gray-900 hover:bg-gray-800 rounded-lg p-6 transition-colors border border-gray-800 hover:border-orange-500"
              >
                <div className="flex items-start space-x-4">
                  {/* Cover */}
                  <div className="flex-shrink-0">
                    {novel.cover_url ? (
                      <img
                        src={novel.cover_url}
                        alt={novel.title}
                        className="w-24 h-32 object-cover rounded-lg"
                      />
                    ) : (
                      <div className="w-24 h-32 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                        <BookOpen className="w-12 h-12 text-white opacity-50" />
                      </div>
                    )}
                  </div>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xl font-bold text-white mb-2 line-clamp-1">
                      {novel.title}
                    </h3>
                    <p className="text-gray-400 mb-3 line-clamp-2">
                      {novel.description}
                    </p>
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <span>{novel.genres?.join(', ')}</span>
                      <span>•</span>
                      <span>{novel.chapter_count || 0} chapters</span>
                      <span>•</span>
                      <span>{novel.status || 'Ongoing'}</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NovelsPage;
